import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Trash2, Loader2, ChevronDown, Copy, Check, RefreshCw,
  Users, Globe, Mail, ExternalLink, X, AlertCircle, Zap,
  Share2, Code, TrendingUp, Heart,
} from 'lucide-react';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralNetwork() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [impact, setImpact] = useState(null);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [form, setForm] = useState({ partner_name: '', partner_profession: '', partner_website: '', partner_email: '' });
  const [expandedPartner, setExpandedPartner] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [regenerating, setRegenerating] = useState(null);

  const fetchData = async () => {
    try {
      const [netRes, impactRes] = await Promise.all([
        axios.get(`${API}/referral-network`, { withCredentials: true }),
        axios.get(`${API}/referral-network/impact`, { withCredentials: true }),
      ]);
      setPartners(netRes.data.partners || []);
      setMeta(netRes.data);
      setImpact(impactRes.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [navigate]);

  const handleAdd = async () => {
    if (!form.partner_name.trim() || !form.partner_profession.trim()) return;
    setAdding(true);
    setAddError('');
    try {
      const res = await axios.post(`${API}/referral-network`, form, { withCredentials: true });
      setPartners(prev => [res.data, ...prev]);
      setForm({ partner_name: '', partner_profession: '', partner_website: '', partner_email: '' });
      setShowAdd(false);
      // Refresh impact
      const impRes = await axios.get(`${API}/referral-network/impact`, { withCredentials: true });
      setImpact(impRes.data);
    } catch (e) {
      setAddError(e.response?.data?.detail || 'Failed to add partner');
    }
    setAdding(false);
  };

  const handleDelete = async (partnerId) => {
    try {
      await axios.delete(`${API}/referral-network/${partnerId}`, { withCredentials: true });
      setPartners(prev => prev.filter(p => p.partner_id !== partnerId));
      const impRes = await axios.get(`${API}/referral-network/impact`, { withCredentials: true });
      setImpact(impRes.data);
    } catch {}
  };

  const handleRegenerate = async (partnerId) => {
    setRegenerating(partnerId);
    try {
      const res = await axios.post(`${API}/referral-network/${partnerId}/regenerate`, {}, { withCredentials: true });
      setPartners(prev => prev.map(p =>
        p.partner_id === partnerId ? { ...p, mutual_content: res.data.mutual_content } : p
      ));
    } catch {}
    setRegenerating(null);
  };

  const copyText = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AppShell active="intel">
      <div className="p-5 max-w-3xl mx-auto pb-28" data-testid="referral-network-page">
        <h1 className="font-display text-2xl font-medium text-[#3C3226] tracking-tight mb-1">Referral Network</h1>
        <p className="text-sm text-[#5C5347] mb-4 leading-relaxed">
          Build a local network of professionals who refer clients to each other — boosting your AI recommendations.
        </p>

        {/* Impact card */}
        {impact && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-none mb-4"
            style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}
            data-testid="referral-impact"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap size={14} className="text-[#1D6B4F]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#1D6B4F]">Network Impact</span>
              </div>
              <span className="text-lg font-bold text-[#1D6B4F]">+{impact.estimated_boost} pts</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center">
                <p className="text-lg font-bold text-[#3C3226]">{impact.active_partners}</p>
                <p className="text-[10px] text-[#5C5347]">Partners</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-[#3C3226]">{impact.partners_with_website}</p>
                <p className="text-[10px] text-[#5C5347]">With Website</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-[#3C3226]">{impact.professions_covered}</p>
                <p className="text-[10px] text-[#5C5347]">Professions</p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="h-2 bg-[#1D6B4F]/10 rounded-full overflow-hidden mb-2">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(impact.estimated_boost / impact.max_boost) * 100}%` }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="h-full bg-[#1D6B4F] rounded-full"
              />
            </div>
            <p className="text-[10px] text-[#5C5347]">Estimated boost: {impact.estimated_boost}/{impact.max_boost} points</p>
            {impact.tips?.length > 0 && (
              <p className="text-[11px] text-[#1D6B4F] mt-2 leading-relaxed">{impact.tips[0]}</p>
            )}
          </motion.div>
        )}

        {/* Add partner */}
        {!showAdd ? (
          <button
            onClick={() => setShowAdd(true)}
            disabled={partners.length >= 10}
            className="w-full p-3 rounded-none border-2 border-dashed border-[#D4C9B8] text-[#5C5347] flex items-center justify-center gap-2 hover:border-[#1D6B4F]/30 hover:text-[#1D6B4F] transition-all mb-4 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="add-partner-btn"
          >
            <Plus size={16} />
            <span className="text-sm font-medium">
              {partners.length >= 10 ? 'Max 10 partners' : 'Add referral partner'}
            </span>
          </button>
        ) : (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="overflow-hidden mb-4"
          >
            <div className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] space-y-2.5" data-testid="add-partner-form">
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={form.partner_name}
                  onChange={e => { setForm(f => ({ ...f, partner_name: e.target.value })); setAddError(''); }}
                  placeholder="Business name *"
                  className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226]"
                  data-testid="partner-name-input"
                  autoFocus
                />
                <input
                  type="text"
                  value={form.partner_profession}
                  onChange={e => setForm(f => ({ ...f, partner_profession: e.target.value }))}
                  placeholder="Their profession *"
                  className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226]"
                  data-testid="partner-profession-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="url"
                  value={form.partner_website}
                  onChange={e => setForm(f => ({ ...f, partner_website: e.target.value }))}
                  placeholder="Website (optional)"
                  className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226]"
                  data-testid="partner-website-input"
                />
                <input
                  type="email"
                  value={form.partner_email}
                  onChange={e => setForm(f => ({ ...f, partner_email: e.target.value }))}
                  placeholder="Email (optional)"
                  className="rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#3C3226]"
                  data-testid="partner-email-input"
                />
              </div>
              {addError && (
                <div className="flex items-center gap-1.5 text-xs text-[#E24B4A]">
                  <AlertCircle size={12} /> {addError}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowAdd(false); setAddError(''); }}
                  className="flex-1 rounded-full border border-[#D4C9B8] text-[#5C5347] py-2.5 text-sm font-medium hover:bg-[#EDE8DF] transition-colors"
                  data-testid="cancel-add-partner-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={adding || !form.partner_name.trim() || !form.partner_profession.trim()}
                  className="flex-[2] rounded-none bg-[#3C3226] hover:opacity-85 disabled:opacity-40 text-[#F0EBE3] py-2.5 text-sm font-medium transition-all flex items-center justify-center gap-2"
                  data-testid="confirm-add-partner-btn"
                >
                  {adding ? <Loader2 size={14} className="animate-spin" /> : <Heart size={14} />}
                  {adding ? 'Creating referral...' : 'Add to network'}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Partner list */}
        {partners.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-none bg-[#F7F4EF] flex items-center justify-center mx-auto mb-3">
              <Users size={24} className="text-[#5C5347]" />
            </div>
            <p className="text-sm font-medium text-[#3C3226] mb-1">No referral partners yet</p>
            <p className="text-xs text-[#5C5347] max-w-xs mx-auto">
              Add local professionals you trust — plumbers partner with electricians, dentists with orthodontists. Cross-referrals boost AI recommendations for everyone.
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="partner-list">
            {partners.map((partner, idx) => {
              const isExpanded = expandedPartner === partner.partner_id;
              const content = partner.mutual_content || {};

              return (
                <motion.div
                  key={partner.partner_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="rounded-none bg-[#F7F4EF] border border-[#D4C9B8] overflow-hidden"
                  data-testid={`partner-${partner.partner_id}`}
                >
                  {/* Header */}
                  <button
                    onClick={() => setExpandedPartner(isExpanded ? null : partner.partner_id)}
                    className="w-full p-4 flex items-center gap-3 text-left"
                  >
                    <div className="w-10 h-10 rounded-sm bg-[#1D6B4F]/8 flex items-center justify-center flex-shrink-0">
                      <Users size={16} className="text-[#1D6B4F]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#3C3226] truncate">{partner.partner_name}</p>
                      <div className="flex items-center gap-2 text-[11px] text-[#5C5347]">
                        <span>{partner.partner_profession}</span>
                        {partner.partner_website && (
                          <>
                            <span className="text-[#5C5347]/30">|</span>
                            <Globe size={10} className="text-[#1D6B4F]" />
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] font-medium text-[#1D6B4F] bg-[#1D6B4F]/8 px-2 py-0.5 rounded-full">Active</span>
                      <ChevronDown size={14} className={`text-[#5C5347] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </button>

                  {/* Expanded */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 space-y-3">
                          {/* Your recommendation (to paste on your site) */}
                          {content.your_recommendation && (
                            <div className="p-3 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5347]">Your recommendation (post on your site)</span>
                                <button
                                  onClick={() => copyText(content.your_recommendation, `rec_${partner.partner_id}`)}
                                  className="p-1 hover:bg-[#EDE8DF] rounded"
                                  data-testid="copy-your-rec-btn"
                                >
                                  {copiedField === `rec_${partner.partner_id}` ? <Check size={12} className="text-[#1D6B4F]" /> : <Copy size={12} className="text-[#5C5347]" />}
                                </button>
                              </div>
                              <p className="text-xs text-[#3C3226] leading-relaxed">{content.your_recommendation}</p>
                            </div>
                          )}

                          {/* Their recommendation (share with partner) */}
                          {content.their_recommendation && (
                            <div className="p-3 rounded-sm bg-[#1D6B4F]/3 border border-[#1D6B4F]/10">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-[#1D6B4F]">For your partner to post</span>
                                <button
                                  onClick={() => copyText(content.their_recommendation, `their_${partner.partner_id}`)}
                                  className="p-1 hover:bg-[#EDE8DF]/50 rounded"
                                  data-testid="copy-their-rec-btn"
                                >
                                  {copiedField === `their_${partner.partner_id}` ? <Check size={12} className="text-[#1D6B4F]" /> : <Copy size={12} className="text-[#5C5347]" />}
                                </button>
                              </div>
                              <p className="text-xs text-[#3C3226] leading-relaxed">{content.their_recommendation}</p>
                            </div>
                          )}

                          {/* Social post */}
                          {content.social_post && (
                            <div className="p-3 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-1.5">
                                  <Share2 size={10} className="text-[#C67A5C]" />
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5347]">Social post</span>
                                </div>
                                <button
                                  onClick={() => copyText(content.social_post, `social_${partner.partner_id}`)}
                                  className="p-1 hover:bg-[#EDE8DF] rounded"
                                  data-testid="copy-social-btn"
                                >
                                  {copiedField === `social_${partner.partner_id}` ? <Check size={12} className="text-[#1D6B4F]" /> : <Copy size={12} className="text-[#5C5347]" />}
                                </button>
                              </div>
                              <p className="text-xs text-[#3C3226] leading-relaxed">{content.social_post}</p>
                            </div>
                          )}

                          {/* Badge embed code */}
                          {partner.badge_code && (
                            <div className="p-3 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-1.5">
                                  <Code size={10} className="text-[#5C5347]" />
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C5347]">Website badge</span>
                                </div>
                                <button
                                  onClick={() => copyText(partner.badge_code, `badge_${partner.partner_id}`)}
                                  className="p-1 hover:bg-[#EDE8DF] rounded"
                                  data-testid="copy-badge-btn"
                                >
                                  {copiedField === `badge_${partner.partner_id}` ? <Check size={12} className="text-[#1D6B4F]" /> : <Copy size={12} className="text-[#5C5347]" />}
                                </button>
                              </div>
                              <code className="text-[10px] text-[#5C5347] block truncate">{partner.badge_code}</code>
                              {/* Badge preview */}
                              <div className="mt-2 p-2 bg-[#F7F4EF] rounded-none border border-[#D4C9B8]">
                                <p className="text-[10px] text-[#5C5347] mb-1">Preview:</p>
                                <div dangerouslySetInnerHTML={{ __html: partner.badge_code }} />
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => handleRegenerate(partner.partner_id)}
                              disabled={regenerating === partner.partner_id}
                              className="flex items-center gap-1.5 text-xs text-[#1D6B4F] hover:underline"
                              data-testid="regenerate-content-btn"
                            >
                              {regenerating === partner.partner_id
                                ? <><Loader2 size={11} className="animate-spin" /> Regenerating...</>
                                : <><RefreshCw size={11} /> Refresh content</>}
                            </button>
                            <button
                              onClick={() => handleDelete(partner.partner_id)}
                              className="flex items-center gap-1.5 text-xs text-[#E24B4A]/60 hover:text-[#E24B4A] transition-colors"
                              data-testid="remove-partner-btn"
                            >
                              <Trash2 size={12} /> Remove
                            </button>
                          </div>

                          {/* Partner contact info */}
                          {(partner.partner_website || partner.partner_email) && (
                            <div className="flex items-center gap-3 pt-1 border-t border-[#D4C9B8]">
                              {partner.partner_website && (
                                <a
                                  href={partner.partner_website.startsWith('http') ? partner.partner_website : `https://${partner.partner_website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-[11px] text-[#1D6B4F] hover:underline"
                                  data-testid="partner-website-link"
                                >
                                  <ExternalLink size={10} /> Website
                                </a>
                              )}
                              {partner.partner_email && (
                                <a
                                  href={`mailto:${partner.partner_email}`}
                                  className="flex items-center gap-1 text-[11px] text-[#1D6B4F] hover:underline"
                                  data-testid="partner-email-link"
                                >
                                  <Mail size={10} /> Email
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Weekly Digest Preview Link */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="mt-6 p-4 rounded-none bg-[#EF9F27]/5 border border-[#EF9F27]/15"
          data-testid="digest-promo"
        >
          <div className="flex items-center gap-2 mb-1">
            <Mail size={14} className="text-[#EF9F27]" />
            <span className="text-xs font-bold text-[#3C3226]">Weekly Email Digest</span>
            <span className="text-[9px] font-medium text-[#EF9F27] bg-[#EF9F27]/10 px-1.5 py-0.5 rounded-full">Coming Soon</span>
          </div>
          <p className="text-[11px] text-[#5C5347] leading-relaxed">
            Get weekly summaries of your score changes, competitor movements, gaps closed, and top actions — delivered to your inbox.
          </p>
          <button
            onClick={() => navigate('/digest-preview')}
            className="mt-2 text-xs font-medium text-[#EF9F27] hover:underline flex items-center gap-1"
            data-testid="preview-digest-btn"
          >
            Preview your digest <ChevronDown size={10} className="-rotate-90" />
          </button>
        </motion.div>
      </div>
    </AppShell>
  );
}
